"use client"

import { useState, useRef, useEffect } from "react"
import "../css/ccrm.css"

const API_URL = "/api/leads/bulk"

const Main = () => {
  const [csvName, setCsvName] = useState("")
  const [headers, setHeaders] = useState([])
  const [rows, setRows] = useState([])
  const [payload, setPayload] = useState([])
  const [error, setError] = useState("")
  const [sending, setSending] = useState(false)
  const [toast, setToast] = useState(null)
  const [successPopup, setSuccessPopup] = useState(false)
  const fileRef = useRef(null)

  const ALIAS = {
    fecha: ["fecha de creacion", "fecha de creación", "fecha creacion", "fecha"],
    nombre: ["nombre"],
    correo: ["correo electronico", "correo electrónico", "email", "e-mail", "correo"],
    telefono1: ["telefono", "teléfono"],
    telefono2: [
      "numero de telefono secundario",
      "número de teléfono secundario",
      "telefono secundario",
      "tel secundario",
    ],
    whatsapp: ["numero de whatsapp", "número de whatsapp", "whatsapp"],
    canal: ["canal"],
    formulario: ["formulario"],
    etiquetas: ["etiquetas", "tags"],
  }

  function buildPayload(fromRows) {
    return fromRows.map((r) => {
      const fecha = getFieldByAliases(r, ALIAS.fecha)
      const nombre = getFieldByAliases(r, ALIAS.nombre)
      const correo = getFieldByAliases(r, ALIAS.correo)
      const tel1 = getFieldByAliases(r, ALIAS.telefono1)
      const tel2 = getFieldByAliases(r, ALIAS.telefono2)
      const wapp = getFieldByAliases(r, ALIAS.whatsapp)
      const canal = getFieldByAliases(r, ALIAS.canal)
      const formulario = getFieldByAliases(r, ALIAS.formulario)
      const etiquetasRaw = getFieldByAliases(r, ALIAS.etiquetas)

      return {
        nombre: [fecha, nombre].filter(Boolean).join(" "),
        cliente: nombre || "",
        correo_electronico: correo || "",
        telefonos: collectPhones(tel1, tel2, wapp),
        origen: "Facebook",
        canal: canal || "",
        formulario: formulario || "",
        etiquetas: splitAndCleanTags(etiquetasRaw),
      }
    })
  }

  useEffect(() => {
    if (successPopup) {
      // Hide popup after 10 seconds
      const hideTimer = setTimeout(() => {
        setSuccessPopup(false)
      }, 10000)

      // Clear data after 20 seconds
      const clearTimer = setTimeout(() => {
        console.log("[v0] Auto-clearing data after 20 seconds")
        handleClear()
      }, 20000)

      return () => {
        clearTimeout(hideTimer)
        clearTimeout(clearTimer)
      }
    }
  }, [successPopup])

  const handleFile = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setError("")
    setToast(null)
    setCsvName(file.name)

    const reader = new FileReader()
    reader.onload = () => {
      try {
        const text = reader.result?.toString() || ""
        const { headers: h, rows: r } = parseCSV(text)
        setHeaders(h)
        setRows(r)
        setPayload(buildPayload(r))
      } catch (err) {
        console.error(err)
        setError("No se pudo leer el CSV. Verifica el formato.")
      }
    }
    reader.onerror = () => setError("Error leyendo el archivo.")
    reader.readAsText(file, "utf-8")
  }

  const handleClear = () => {
    setCsvName("")
    setHeaders([])
    setRows([])
    setPayload([])
    setError("")
    setToast(null)
    setSuccessPopup(false)
    if (fileRef.current) fileRef.current.value = ""
  }

  const handleSend = async () => {
    if (!payload.length) {
      setToast({ ok: false, msg: "No hay leads para enviar." })
      return
    }
    setSending(true)
    setToast(null)
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        mode: "cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ leads: payload }),
      })
      if (!res.ok) throw new Error("Error al enviar.")
      setSuccessPopup(true)
      setToast({ ok: true, msg: `¡Listo! Se enviaron ${payload.length} lead(s).` })
    } catch (err) {
      setToast({ ok: false, msg: err.message || "Error al enviar." })
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="crm-container">
      {successPopup && (
        <div className="success-popup">
          <div className="success-popup-content">
            <div className="success-icon">✓</div>
            <div className="success-text">Envío exitoso</div>
          </div>
        </div>
      )}

      <header className="hero">
        <div className="hero-title">Carga de Leads</div>
        <div className="hero-subtitle">CSV → Vista previa → Envío</div>
      </header>

      <section className="card">
        <div className="uploader">
          <label className="file-label" htmlFor="csvInput">
            <span className="file-label-title">Seleccionar CSV</span>
            <small className="file-label-note">Formato: UTF-8, separado por comas. Con o sin comillas.</small>
          </label>
          <div className="file-input-wrapper">
            <input id="csvInput" ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleFile} />
            <div className="file-input-display">
              <div className="file-input-button">Buscar</div>
              <div className="file-input-text">{csvName || "seleccionar archivo"}</div>
            </div>
          </div>
        </div>

        <div className="summary">
          <div className={`badge ${rows.length ? "" : "badge-empty"}`}>
            {rows.length ? `${rows.length} fila(s) detectadas` : "Sin datos"}
          </div>
          {rows.length > 0 && (
            <div className="hint">
              <span>Origen fijo:</span> Facebook &nbsp;•&nbsp; <span>Listo para enviar:</span> {payload.length} lead(s)
            </div>
          )}
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {toast && <div className={`alert ${toast.ok ? "alert-success" : "alert-error"}`}>{toast.msg}</div>}

        {rows.length > 0 && (
          <>
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    {headers.map((h, i) => (
                      <th key={i}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, ri) => (
                    <tr key={ri}>
                      {headers.map((h, hi) => (
                        <td key={hi}>{r[h] || ""}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="actions">
              <button
                className="btn btn-primary"
                onClick={handleSend}
                disabled={sending || !payload.length}
                title="Enviar a la API de leads"
              >
                {sending ? "Enviando..." : "Enviar"}
              </button>
              <button className="btn btn-ghost" onClick={handleClear}>
                Limpiar
              </button>
            </div>
          </>
        )}
      </section>

      <footer className="footnote">
        Consejo: si "Enviar" falla, puede ser por CORS o formato del CSV. Revisa nombres de columnas y que el endpoint
        acepte <code>Content-Type: application/json</code>.
      </footer>
    </div>
  )
}

export default Main

function removeAccents(str = "") {
  try {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  } catch {
    return str
  }
}
function normKey(str = "") {
  return removeAccents(String(str).toLowerCase().trim())
}

function parseCSV(text) {
  const rows = []
  let field = ""
  let row = []
  let inQuotes = false

  for (let i = 0; i < text.length; i++) {
    const c = text[i]
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        field += c
      }
    } else {
      if (c === '"') inQuotes = true
      else if (c === ",") {
        row.push(field)
        field = ""
      } else if (c === "\r") {
        /* skip */
      } else if (c === "\n") {
        row.push(field)
        rows.push(row)
        row = []
        field = ""
      } else field += c
    }
  }
  if (field !== "" || row.length) {
    row.push(field)
    rows.push(row)
  }
  const headers = (rows.shift() || []).map((h) => h.trim())
  const objects = rows
    .filter((r) => r.some((cell) => String(cell).trim() !== ""))
    .map((r) => {
      const obj = {}
      headers.forEach((h, idx) => {
        obj[h] = (r[idx] ?? "").toString().trim()
      })
      return obj
    })
  return { headers, rows: objects }
}

function getFieldByAliases(obj, aliases) {
  const map = {}
  Object.keys(obj).forEach((k) => (map[normKey(k)] = obj[k]))
  for (const a of aliases) {
    if (map[a] !== undefined && String(map[a]).trim() !== "") return map[a]
  }
  return ""
}

function splitAndCleanTags(raw) {
  if (!raw) return []
  return Array.from(
    new Set(
      String(raw)
        .split(/[;,|/]/g)
        .map((t) => t.trim())
        .filter(Boolean),
    ),
  )
}

function sanitizePhone(raw) {
  if (!raw) return ""
  return String(raw).replace(/[^\d+]/g, "")
}
function collectPhones(...vals) {
  const all = []
  vals.forEach((v) => {
    if (!v) return
    String(v)
      .split(/[;,/|]/g)
      .forEach((p) => {
        const s = sanitizePhone(p)
        if (s) all.push(s)
      })
  })
  return Array.from(new Set(all)).filter(Boolean)
}
